/**
 * NF REST 基础请求层。
 * - 全部业务调用走 session cookie: credentials:'include' (跨站需后端 SameSite=None;Secure)。
 * - dev: VITE_API_BASE 空 → 相对路径走 Bun serve-dist 同源反代。
 * - prod 跨站: VITE_API_BASE = 后端域。
 */
import i18n from '@/i18n'
import { toast } from 'sonner'
import { DEFAULT_BRANCH_ID, useUiStore } from '@/stores/uiStore'

export const API_BASE = import.meta.env?.VITE_API_BASE ?? ''

export class ApiError extends Error {
  status: number
  code?: string
  data?: unknown
  constructor(status: number, message: string, code?: string, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.data = data
  }
}

type UnauthorizedHandler = () => void

let onUnauthorized: UnauthorizedHandler | null = null

const localizedHttpStatuses = new Set([401, 403, 404, 429, 500, 502, 503])

function localizedHttpFallback(status: number): string {
  const key = localizedHttpStatuses.has(status) ? `errors.http.${status}` : 'errors.http.unknown'
  return i18n.t(key)
}

// 用户可见错误文案的 SSOT 是前端 i18n `errors.codes`；后端 message 仅用于日志和调试，
// 不直接展示给用户。调整产品文案时请修改对应的前端 error code 翻译。
export function friendlyMessage(error: unknown, fallback?: string): string {
  if (!(error instanceof ApiError)) return fallback ?? i18n.t('errors.http.unknown')

  if (error.code) {
    const codeKey = `errors.codes.${error.code}`
    if (i18n.exists(codeKey)) return i18n.t(codeKey)
  }

  return localizedHttpFallback(error.status)
}

export function setOnUnauthorized(fn: UnauthorizedHandler | null) {
  onUnauthorized = fn
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Bearer token (admin 专用; 业务请求不传, 走 cookie) */
  bearer?: string
  headers?: Record<string, string>
  /** 例如登录接口密码错误时仍应停留在当前页面展示表单错误。 */
  skipAuthRedirect?: boolean
  /** GET 读面按需携带 branch_id; true 时取 uiStore.currentBranchId。 */
  withBranch?: boolean
  /** 显式 branch_id 覆盖; 仅 GET 自动注入查询参数。 */
  branchId?: string | null
}

export function getCurrentBranchId(): string {
  return useUiStore.getState().currentBranchId || DEFAULT_BRANCH_ID
}

export function withBranch(path: string, branchId = getCurrentBranchId()): string {
  const [base, hash = ''] = path.split('#', 2)
  const [pathname, query = ''] = base.split('?', 2)
  const qs = new URLSearchParams(query)
  if (!qs.has('branch_id')) qs.set('branch_id', branchId || DEFAULT_BRANCH_ID)
  const suffix = hash ? `#${hash}` : ''
  return `${pathname}?${qs.toString()}${suffix}`
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, bearer, headers = {}, skipAuthRedirect = false, withBranch: shouldAttachBranch = false, branchId } = opts
  const h: Record<string, string> = { ...headers }
  if (body !== undefined) h['Content-Type'] = 'application/json'
  if (bearer) h['Authorization'] = `Bearer ${bearer}`
  const requestPath = method === 'GET' && (shouldAttachBranch || branchId) ? withBranch(path, branchId ?? getCurrentBranchId()) : path

  const res = await fetch(`${API_BASE}${requestPath}`, {
    method,
    headers: h,
    // 业务请求走 session cookie; admin Bearer 请求不携带 cookie, 保持两种凭证互斥。
    credentials: bearer ? 'omit' : 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown = undefined
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!res.ok) {
    let msg = res.statusText
    let code: string | undefined
    if (data && typeof data === 'object') {
      const o = data as Record<string, unknown>
      if (typeof o.error === 'string') msg = o.error
      else if (o.error && typeof o.error === 'object') {
        const nestedError = o.error as Record<string, unknown>
        if (typeof nestedError.message === 'string') msg = nestedError.message
        if (typeof nestedError.code === 'string') code = nestedError.code
      }
      else if (typeof o.message === 'string') msg = o.message
      if (typeof o.code === 'string') code = o.code
    }
    if (msg === res.statusText) msg = localizedHttpFallback(res.status)
    if (res.status === 401 && !skipAuthRedirect) {
      try {
        onUnauthorized?.()
      } catch (err) {
        console.warn('Unauthorized handler failed', err)
      }
    }
    throw new ApiError(res.status, msg, code, data)
  }
  if (method !== 'GET') notifyProjectBudgetGate(data)
  return data as T
}

export function notifyProjectBudgetGate(data: unknown): void {
  if (data && typeof data === 'object' && 'project_budget_gate' in data && data.project_budget_gate === 'yellow') {
    toast.warning(i18n.t('projectBudget.warning'))
  }
}
